import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, Search, FileSignature, Pencil, Trash2, EyeOff, Eye } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { t, formatMoney } from "@/i18n";
import { api } from "@/lib/api";
import { PROPERTY_TYPES, propertyTypeLabel } from "@/lib/propertyTypes";
import TranslateButton from "@/components/TranslateButton";

const inputCls = "border-[#D8CFBB] bg-[#FAFAF8]";

function EditMandateDialog({ mandate, open, onClose, onSaved }) {
  const { lang } = useApp();
  const [area, setArea] = useState(mandate.search_area || "");
  const [budget, setBudget] = useState(mandate.search_budget || "");
  const [ref, setRef] = useState(mandate.property_ref || "");
  const [terms, setTerms] = useState(mandate.commission_terms || "");
  const [months, setMonths] = useState(mandate.exclusivity_months || 6);
  const [ptype, setPtype] = useState(mandate.property_type || "");
  const [busy, setBusy] = useState(false);
  const isSale = mandate.mandate_type === "sale";

  const submit = async () => {
    setBusy(true);
    try {
      const payload = isSale
        ? { property_ref: ref, commission_terms: terms, exclusivity_months: parseInt(months, 10) || 6, property_type: ptype || null }
        : {
            search_area: area,
            search_budget: parseFloat(budget) || 0,
            property_ref: ref,
            commission_terms: terms,
            exclusivity_months: parseInt(months, 10) || 6,
            property_type: ptype || null,
          };
      await api.patch(`/mandates/${mandate.id}`, payload);
      toast.success("Mandato aggiornato");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-[#E5E5E3] max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`edit-mandate-dialog-${mandate.id}`}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            {isSale ? t("edit_sale_mandate", lang) : t("edit_search_mandate", lang)}
          </DialogTitle>
        </DialogHeader>
        <div>
          <Label className="text-[#4A4A4A]">{isSale ? "Tipo di immobile" : "Tipo di immobile cercato"}</Label>
          <Select value={ptype} onValueChange={setPtype}>
            <SelectTrigger data-testid="edit-mandate-type" className={inputCls}>
              <SelectValue placeholder="Seleziona il tipo…" />
            </SelectTrigger>
            <SelectContent>
              {PROPERTY_TYPES.map((pt) => (
                <SelectItem key={pt.value} value={pt.value}>{pt[lang] || pt.it}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isSale ? (
          <>
            <div>
              <Label className="text-[#4A4A4A]">Riferimento immobile</Label>
              <Input data-testid="edit-mandate-ref" value={ref} onChange={(e) => setRef(e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-[#4A4A4A]">Termini di commissione</Label>
              <Input data-testid="edit-mandate-terms" value={terms} onChange={(e) => setTerms(e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-[#4A4A4A]">Durata esclusiva (mesi)</Label>
              <Input data-testid="edit-mandate-months" type="number" min={1} value={months} onChange={(e) => setMonths(e.target.value)} className={inputCls} />
            </div>
          </>
        ) : (
          <>
            <div>
              <Label className="text-[#4A4A4A]">Zona di ricerca</Label>
              <Input data-testid="edit-mandate-area" value={area} onChange={(e) => setArea(e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-[#4A4A4A]">Budget (€)</Label>
              <Input data-testid="edit-mandate-budget" type="number" value={budget} onChange={(e) => setBudget(e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-[#4A4A4A]">Durata mandato (mesi)</Label>
              <Input data-testid="edit-mandate-months" type="number" min={1} value={months} onChange={(e) => setMonths(e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-[#4A4A4A]">Termini di commissione</Label>
              <Input data-testid="edit-mandate-terms" value={terms} onChange={(e) => setTerms(e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-[#4A4A4A]">Descrizione / note</Label>
              <Textarea data-testid="edit-mandate-notes" rows={4} value={ref} onChange={(e) => setRef(e.target.value)}
                        placeholder="Tipologia cercata, caratteristiche, esigenze specifiche…"
                        className="border-[#D8CFBB] bg-[#FAFAF8]" />
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#6B6B6B]">{t("cancel", lang)}</Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid={`edit-mandate-save-${mandate.id}`}
            className="bg-[#B8965A] hover:bg-[#a08248] text-white rounded-full"
          >
            {busy ? "…" : t("save", lang)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MandateCard({ mandate, onDownload, onChanged, isAdmin = false }) {
  const { lang, user } = useApp();
  const isSale = mandate.mandate_type === "sale";
  const isOwner = user?.user_id === mandate.user_id;
  const canManage = isOwner || isAdmin;
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const doDelete = async () => {
    try {
      await api.delete(`/mandates/${mandate.id}`);
      toast.success(isSale ? "Mandato eliminato" : "Ricerca eliminata");
      setConfirmOpen(false);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="rounded-lg border border-[#D8CFBB] bg-[#F5F0E6] p-5" data-testid={`mandate-card-${mandate.id}`}>
      {mandate.property_type && (
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#B8965A] mb-1.5" data-testid={`mandate-type-label-${mandate.id}`}>
          {propertyTypeLabel(mandate.property_type, lang)}
        </p>
      )}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {isSale ? <FileSignature className="h-4 w-4 text-[#C5A880]" /> : <Search className="h-4 w-4 text-[#C5A880]" />}
          <p className="text-sm text-[#1A1610] font-medium">
            {isSale ? t("mandate_sale_kind", lang) : t("mandate_search_kind", lang)}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {mandate.admin_hidden && (
            <Badge className="bg-red-100 text-red-800 border border-red-200 text-[10px]">
              <EyeOff className="h-3 w-3 me-1" /> HIDDEN
            </Badge>
          )}
          <Badge className="bg-emerald-600 text-white text-[10px]">SIGNED</Badge>
        </div>
      </div>
      <p className="text-xs text-[#7A6E5A] mb-1">
        {mandate.signer_name} · <span className="font-mono">{mandate.signer_email}</span>
      </p>
      {isSale && <p className="text-xs text-[#9A8B72] mb-3 line-clamp-2">{mandate.property_ref || "—"}</p>}
      {!isSale && (
        <p className="text-xs text-[#9A8B72] mb-3">
          {mandate.search_area || "—"} · {mandate.search_budget ? formatMoney(mandate.search_budget, lang) : "—"}
        </p>
      )}
      {mandate.admin_hidden && mandate.rejection_reason && (
        <p className="text-[11px] text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1 mb-3">
          {mandate.rejection_reason}
        </p>
      )}
      <div className="flex items-center justify-between pt-3 border-t border-[#E5DDC9] gap-2 flex-wrap">
        <p className="text-[11px] text-[#9A8B72] font-mono">{new Date(mandate.created_at).toLocaleDateString()}</p>
        <div className="flex gap-1.5">
          {!isSale && (
            <Link to={`/searches/${mandate.id}`} data-testid={`mandate-view-${mandate.id}`}>
              <Button
                size="sm"
                className="bg-transparent border border-[#C5A880]/40 text-[#C5A880] hover:bg-[#C5A880]/10 rounded-full"
              >
                <Eye className="h-3.5 w-3.5" />
              </Button>
            </Link>
          )}
          <Button
            size="sm" onClick={() => onDownload(mandate)}
            data-testid={`mandate-pdf-${mandate.id}`}
            className="bg-transparent border border-[#C5A880]/40 text-[#C5A880] hover:bg-[#C5A880]/10 rounded-full"
          >
            <FileDown className="h-3.5 w-3.5" />
          </Button>
          <TranslateButton kind="mandate" id={mandate.id} />
          {canManage && (
            <>
              <Button
                size="sm" onClick={() => setEditOpen(true)}
                data-testid={`mandate-edit-${mandate.id}`}
                className="bg-transparent border border-[#B8965A]/40 text-[#B8965A] hover:bg-[#F5EDDA] rounded-full"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm" onClick={() => setConfirmOpen(true)}
                data-testid={`mandate-delete-${mandate.id}`}
                className="bg-transparent border border-red-300 text-red-700 hover:bg-red-50 rounded-full"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>

      {editOpen && (
        <EditMandateDialog
          mandate={mandate}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); onChanged?.(); }}
        />
      )}

      <Dialog open={confirmOpen} onOpenChange={(v) => !v && setConfirmOpen(false)}>
        <DialogContent className="bg-white border-[#E5E5E3] max-w-sm" data-testid={`mandate-confirm-delete-${mandate.id}`}>
          <DialogHeader>
            <DialogTitle className="text-xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              {isSale ? t("confirm_delete_sale_mandate", lang) : t("confirm_delete_search_mandate", lang)}
            </DialogTitle>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} className="text-[#6B6B6B]">{t("cancel", lang)}</Button>
            <Button
              onClick={doDelete}
              data-testid={`mandate-confirm-yes-${mandate.id}`}
              className="bg-red-700 hover:bg-red-800 text-white rounded-full"
            >
              {t("delete", lang)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
